<template>
  <div class="flex md:justify-between flex-col-reverse md:flex-row">
    <section class="bg-white p-4 md:p-8 lg:p-10 lg:w-1/3 rounded-lg step">
      <h3 class="lg:text-3xl leading-normal font-semibold">Invite your team</h3>
      <p class="text-lg mt-4 lg:mt-6" style="color: #a8a8a8">
        You’re all set! Invite your teammates to start Hiring Board Journey
      </p>

      <div class="mt-4 lg:mt-6" style="color: #3c3c3c">
        <div>
          <p class="text-xl font-semibold">Import from</p>

          <div class="flex items-center gap-4 w-full mt-2 mb-6">
            <img
              class="cursor-pointer w-12 h-12 step-box p-2"
              src="@/assets/images/icons/app-marketplace/integrations/slack.svg"
            />
            <h5 class="mx-1">or HRIS</h5>
            <img
              class="cursor-pointer w-12 h-12 step-box p-2"
              src="@/assets/images/icons/app-marketplace/integrations/paylocity.svg"
            />
            <img
              class="cursor-pointer w-12 h-12 step-box p-2"
              src="@/assets/images/icons/app-marketplace/integrations/hibob.svg"
            />
            <img
              class="cursor-pointer w-12 h-12 step-box p-2"
              src="@/assets/images/icons/app-marketplace/integrations/personio.svg"
            />
          </div>
        </div>
        <p class="text-xl font-normal">Or enter details manually</p>
        <div class="my-2 flex flex-col gap-4">
          <div>
            <input
              class="w-full p-4 text-xl"
              :class="
                userErrors.email || userErrors.isEmailValid ? 'error-input' : ''
              "
              placeholder="e.g email@email.com"
              v-model="user.email"
              @input="autoFetchUser"
            />
            <p
              v-if="userErrors.email"
              class="text-lg font-normal error-text mt-1"
            >
              Enter email
            </p>

            <p
              v-if="userErrors.isEmailValid"
              class="text-lg font-normal error-text mt-1"
            >
              Enter valid email
            </p>
          </div>
          <div class="flex gap-4">
            <span class="w-full">
              <input
                class="w-full p-4 text-xl"
                :class="userErrors.firstName ? 'error-input' : ''"
                placeholder="First Name"
                v-model="user.firstName"
              />
              <p
                v-if="userErrors.firstName"
                class="text-lg font-normal error-text mt-1"
              >
                Enter first name
              </p>
            </span>

            <span class="w-full">
              <input
                class="w-full p-4 text-xl"
                :class="userErrors.lastName ? 'error-input' : ''"
                placeholder="Last Name"
                v-model="user.lastName"
              />
              <p
                v-if="userErrors.lastName"
                class="text-lg font-normal error-text mt-1"
              >
                Enter last name
              </p>
            </span>
          </div>

          <p v-if="userAlreadyExist" class="text-lg font-normal warn-text -mt-1">
            User already invited.
          </p>
        </div>
        <vs-button
          size="small"
          class="w-max rounded-full text-lg mt-4 py-2 shadow-none"
          @click="addUsersForInvite"
          icon-pack="feather"
          icon="icon-plus"
          :style="{
            color: orgData.primaryThemeColor,
            background: orgData.primaryThemeColor + '30 !important',
          }"
          >Add user</vs-button
        >
      </div>

      <div v-if="usersToInvite.length > 0">
        <hr class="mt-6 mb-4" />
        <div class="flex items-center ml-2">
          <div
            @click.stop
            v-for="(user, i) in usersToInvite.slice(0, 8)"
            :key="i"
            class="-mx-2"
          >
            <vx-tooltip
              class="capitalize"
              :text="`${user.firstName} ${user.lastName}`"
            >
              <vs-avatar
                @click="openRemoveUserPrompt(user, i)"
                size="37px"
                class="m-1"
                v-if="user.profilePictureUrl"
                :src="`${constants.s3Bucket}/editor/profile-pictures/${user.profilePictureUrl}`"
              />
              <vs-avatar
                @click="openRemoveUserPrompt(user, i)"
                v-else
                size="37px"
                class="m-1"
                :src="`https://hostedboringavatars.vercel.app/api/beam?name=${avatarNameGenerator(
                  user.firstName || 'boringAvatar',
                  user.lastName || 'boringAvatar'
                )}`"
              />
            </vx-tooltip>
          </div>

          <div class="-mx-4" @click.stop v-if="usersToInvite.length > 8">
            <vs-dropdown vs-trigger-click :color="orgData.primaryThemeColor">
              <vx-tooltip :text="`${usersToInvite.length - 8} others`">
                <vs-avatar
                  @click.native="stopRouting"
                  size="37px"
                  class="cursor-pointer"
                  :text="`+${usersToInvite.length - 8}`"
                />
              </vx-tooltip>

              <vs-dropdown-menu>
                <vs-input
                  class="m-2"
                  id="vs--input"
                  :color="`${orgData.primaryThemeColor}`"
                  icon-no-border
                  icon="search"
                  icon-size="large"
                  placeholder="Search User"
                  v-model="searchValue"
                  v-on:input="searchUser()"
                  style="width: auto"
                />
                <vs-dropdown-item
                  class="mt-0 cursor-pointer"
                  v-for="(user, index) in searchedUserArray"
                  :key="index"
                  @click="openRemoveUserPrompt(user, index)"
                >
                  <vs-avatar
                    v-if="user.profilePictureUrl"
                    size="40px"
                    class="-m-1 mr-2"
                    @click="openRemoveUserPrompt(user, index)"
                    :src="
                      constants.s3Bucket +
                      '/editor/profile-pictures/' +
                      user.profilePictureUrl
                    "
                  />
                  <vs-avatar
                    v-else
                    size="40px"
                    @click="openRemoveUserPrompt(user, index)"
                    class="-m-1 mr-2"
                    :src="`https://hostedboringavatars.vercel.app/api/beam?name=${avatarNameGenerator(
                      user.firstName,
                      user.lastName
                    )}`"
                  />
                  <p class="text-lg">
                    {{ user.firstName }}
                    {{ user.lastName }}
                  </p>
                </vs-dropdown-item>
              </vs-dropdown-menu>
            </vs-dropdown>
          </div>
        </div>
      </div>

      <p v-if="usersEmpty" class="mt-6 text-lg font-normal error-text">
        Invite teammates to continue
      </p>
      <div class="flex justify-between mt-6">
        <p
          class="flex items-center text-lg md:text-xl cursor-pointer"
          style="color: #a8a8a8"
          @click="
            $router.push(
              `/organization/${orgData.slug}/create-board/${$route.params.boardId}/step3`
            )
          "
        >
          <img src="@/assets/images/icons/arrow-left-grey.svg" alt="Back" />
          Back
        </p>
        <vs-button
          class="w-max rounded-lg text-xl mt-4"
          @click="addToBoard"
          :color="orgData.primaryThemeColor"
          >Finish</vs-button
        >
      </div>
    </section>

    <section
      class="mb-6 md:mb-0 md:ml-8 bg-white p-4 md:p-8 lg:p-10 lg:w-2/3 rounded-lg step"
    >
      <BoardSkeletonVue />
    </section>

    <!-- Remove relationship popup  -->
    <vs-prompt
      title=""
      @close="removeUserPrompt = false"
      :active="removeUserPrompt"
      buttons-hidden
    >
      <vs-col class="pb-4">
        <vs-row
          class="items-center justify-between mt-2 rounded-lg border-user px-4 py-2"
        >
          <vs-row class="w-auto items-center">
            <div>
              <vs-avatar
                v-if="selectedUser.profilePictureUrl"
                size="45px"
                class="-m-1 mr-2"
                :src="
                  constants.s3Bucket +
                  '/editor/profile-pictures/' +
                  selectedUser.profilePictureUrl
                "
              />
              <vs-avatar
                v-else
                size="45px"
                class="-m-1 mr-2"
                :src="`https://hostedboringavatars.vercel.app/api/beam?name=${avatarNameGenerator(
                  selectedUser.firstName || 'boringAvatar',
                  selectedUser.lastName || 'boringAvatar'
                )}`"
              />
            </div>
            <div class="text-left">
              <div class="text-lg font-semibold text-black">
                {{ selectedUser.firstName }}
                {{ selectedUser.lastName }}
              </div>
              <div>
                {{ selectedUser.email }}
              </div>
            </div>
          </vs-row>

          <img
            @click="removeUser"
            class="w-8 h-8 cursor-pointer"
            src="@/assets/images/icons/remove-dash.svg"
            alt="Remove Relationship"
          />
        </vs-row>
      </vs-col>
    </vs-prompt>
  </div>
</template>

<script>
import BoardSkeletonVue from "./component/BoardSkeleton.vue";
import commonMixin from "./mixins/commonMixin";
import constants from "@/constants";

export default {
  data() {
    return {
      usersToInvite: [],
      user: {},
      userErrors: {},
      constants,
      searchedUserArray: [],
      searchValue: "",
      removeUserPrompt: false,
      selectedUser: {},
      usersEmpty: null,
      userAlreadyExist: false,
    };
  },
  components: {
    BoardSkeletonVue,
  },
  mixins: [commonMixin],
  computed: {
    avatarNameGenerator() {
      return this.$store.state.users.avatarNameGenerator;
    },
  },
  watch: {
    usersToInvite: {
      deep: true,
      immediate: true,
      handler(newVal) {
        this.usersEmpty =
          newVal.length > 0 && this.usersEmpty ? false : this.usersEmpty;
      },
    },
  },
  methods: {
    addUsersForInvite() {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      this.userErrors = {
        firstName: !this.user.firstName,
        lastName: !this.user.lastName,
        email: !this.user.email,
        isEmailValid: this.user.email
          ? !emailRegex.test(this.user.email)
          : false,
      };

      const hasErrors = Object.values(this.userErrors).some((error) => error);
      if (!hasErrors) {
        let userExistAt = this.usersToInvite.findIndex(
          (user) => user.email === this.user.email
        );
        if (userExistAt > -1) {
          this.userAlreadyExist = true;
        } else {
          this.usersToInvite.push(this.user);
          this.searchedUserArray = this.usersToInvite;
          this.user = {};
        }
        this.userErrors = {};
      }
    },
    addToBoard() {
      if (this.usersToInvite.length !== 0) {
        for (let i = 0; i < this.usersToInvite.length; i++) {
          let { email, firstName, lastName } = this.usersToInvite[i];
          const payload = {
            boardId: this.$route.params.boardId,
            email: email,
            firstName: firstName,
            lastName: lastName,
          };
          this.$store
            .dispatch("boards/addUserToBoard", payload)
            .then((res) => {})
            .catch((err) => {});
        }
        this.$vs.notify({
          title: "In Progress",
          text: "User On-Boarding is running in background",
          iconPack: "feather",
          icon: "icon-check-circle",
          color: "success",
          fixed: false,
        });
        setTimeout(() => {
          this.$router.push(
            `/organization/${this.orgData.slug}/boards/${this.$route.params.boardId}`
          );
        }, 150);
      } else {
        this.usersEmpty = true;
      }
    },
    searchUser() {
      if (this.searchValue) {
        this.searchedUserArray = this.usersToInvite.filter((user) => {
          if (user.firstName && user.lastName) {
            return (
              user.firstName
                .toLowerCase()
                .includes(this.searchValue.toLowerCase()) ||
              user.lastName
                .toLowerCase()
                .includes(this.searchValue.toLowerCase())
            );
          } else if (user.firstName) {
            return user.firstName
              .toLowerCase()
              .includes(this.searchValue.toLowerCase());
          } else if (user.lastName) {
            return user.lastName
              .toLowerCase()
              .includes(this.searchValue.toLowerCase());
          }
        });
        return;
      } else {
        return (this.searchedUserArray = this.usersToInvite);
      }
    },
    openRemoveUserPrompt(user, index) {
      this.selectedUser = user;
      this.selectedUser.activeIndex = index;
      this.removeUserPrompt = true;
    },
    removeUser() {
      this.usersToInvite.splice(this.selectedUser.activeIndex, 1);
      this.removeUserPrompt = false;
      this.selectedUser = {};
    },
    autoFetchUser() {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      let isEmailVald = emailRegex.test(this.user.email);
      if (isEmailVald) {
        this.$store
          .dispatch("boards/fetchUserByEmail", {
            email: this.user.email,
            installedApps: [],
            organizationId: this.orgData._id,
          })
          .then((userRes) => {
            if (
              userRes &&
              userRes.data &&
              userRes.data.data &&
              userRes.data.data[0]
            ) {
              let { email, firstName, lastName, profilePictureUrl } =
                userRes.data.data[0];
              this.user = {
                email: email,
                firstName: firstName,
                lastName: lastName,
                profilePictureUrl: profilePictureUrl,
              };
            }
          });
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import "./styles/step-common.scss";

.addField {
  border-radius: 12px;
  border: 2px solid #e8ebed;
  color: #a8a8a8;
}
</style>

<style scoped>
.vs-dropdown--item >>> .vs-dropdown--item-link {
  display: flex;
  align-items: center;
  margin: 0.5em 0;
  padding-left: 1em;
  padding-right: 2em;
}

.con-vs-dropdown--menu {
  max-height: 17.5em;
  width: max-content;
  height: auto !important;
  overflow-y: scroll;
}

.con-vs-dropdown--menu::-webkit-scrollbar {
  width: 8px;
}

/* Track */
.con-vs-dropdown--menu::-webkit-scrollbar-track {
  box-shadow: inset 0 0 5px rgba(128, 128, 128, 0.378);
  /* border-radius: 10px; */
}

/* Handle */
.con-vs-dropdown--menu::-webkit-scrollbar-thumb {
  background: rgba(199, 195, 195, 0.658);
  border-radius: 10px;
}

.vs-input >>> .vs-input--icon {
  font-size: 1.5rem;
  color: #97a4bf !important;
}
</style>
